import React from 'react';
import { Subtract } from 'utility-types';

import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { IPublishedConnectionDetailInfo } from '~src/ts/models/IPublishedConnectionInfo';

import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Projects } from '../../projects/resources/Projects';
import { Filter } from '../../ts/helpers/Filter';
import {
  IPublishedConnectionTableDetailInfo,
  IPublishedConnectionTableInfo,
} from '../../ts/models/IPublishedConnectionTableInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { IPublishedConnectionTableActions } from '../../ts/ServerApi/types/IPublishedConnectionTableActions';
import { PublishedConnectionTables } from '../resources/PublishedConnectionTables';

interface InjectedPublishedConnectionTableSpaceData {
  readonly publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  readonly publishedConnectionTableAllowedActions: IPublishedConnectionTableActions;
  readonly publishedConnectionTableDetailInfo: IPublishedConnectionTableDetailInfo;
  readonly publishedConnectionTableInfo: IPublishedConnectionTableInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export interface InjectedPublishedConnectionTableSpaceDataFetcherProps
  extends InjectedPublishedConnectionTableSpaceData {
  readonly refreshData: () => void;
}

export interface PublishedConnectionTableSpaceDataFetcherProps {
  readonly siteName: string;
  readonly publishedConnectionTableId: string;
  readonly siteUrls: SpacesSiteUrls;
  readonly isRequestAccessEnabled: boolean;
}

export function withPublishedConnectionTableSpaceDataFetcher<
  P extends InjectedPublishedConnectionTableSpaceDataFetcherProps
>(Component: React.ComponentType<P>) {
  return function PublishedConnectionTableSpaceDataFetcher(
    props: Subtract<P, InjectedPublishedConnectionTableSpaceDataFetcherProps> &
      PublishedConnectionTableSpaceDataFetcherProps
  ) {
    const [error, setError] = React.useState(false);
    const [injectedData, setInjectedData] =
      React.useState<InjectedPublishedConnectionTableSpaceData>(undefined);
    const [errorCode, setErrorCode] = React.useState(null);

    let didCancel = false;

    const refreshData = () => {
      fetchPublishedConnectionTableSpaceData(props.publishedConnectionTableId)
        .then((injectedData) => {
          !didCancel && setError(false);
          !didCancel && setInjectedData(injectedData);
        })
        .catch((e) => {
          !didCancel && setError(true);
          !didCancel && setErrorCode(e);
        });
    };

    React.useEffect(() => {
      refreshData();
      // This noncompliance existed before we enabled the lint rule. If you’re here, please consider fixing it!
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return () => (didCancel = true);
    }, [props.publishedConnectionTableId, props.siteName]);

    if (error) {
      return (
        <RouterError
          errorType={'publishedConnectionTable'}
          urls={props.siteUrls}
        />
      );
    } else if (!injectedData) {
      return null;
    }

    return (
      <Component
        {...(props as unknown as P)}
        {...injectedData}
        refreshData={refreshData}
      />
    );
  };
}

async function fetchPublishedConnectionTableSpaceData(publishedConnectionTableId: string): Promise<InjectedPublishedConnectionTableSpaceData> {
  const publishedConnectionTableAllowedActions = await PublishedConnectionTables.actions(publishedConnectionTableId);
  const publishedConnectionTableDetailInfo = await PublishedConnectionTables.getById(publishedConnectionTableId);
  const publishedConnectionDetailInfo = await PublishedConnections.getById(publishedConnectionTableDetailInfo.publishedConnectionId);
  const publishedConnectionTableInfo = await PublishedConnectionTables.all()
    .withFilters([Filter.fieldEq('id', publishedConnectionTableId)])
    .getFirst();
  const projectHierarchy = await Projects.getProjectHierarchy(
    publishedConnectionTableDetailInfo.project.id
  );

  return {
    publishedConnectionDetailInfo,
    publishedConnectionTableAllowedActions,
    publishedConnectionTableDetailInfo,
    publishedConnectionTableInfo,
    projectHierarchy,
  };
}
